const imagePreloadCache = new Map();

export function loadImage(src) {
  if (!src) return Promise.resolve({ src, ok: false });

  const cached = imagePreloadCache.get(src);
  if (cached) return cached;

  const pending = new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';

    const finalize = (ok) => {
      resolve({ src, ok });
    };

    img.onload = () => {
      if (typeof img.decode === 'function') {
        img
          .decode()
          .then(() => finalize(true))
          .catch(() => finalize(true));
        return;
      }

      finalize(true);
    };

    img.onerror = () => {
      imagePreloadCache.delete(src);
      finalize(false);
    };

    img.src = src;
  });

  imagePreloadCache.set(src, pending);
  return pending;
}

export function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

// Warm images progressively during idle time. Accepts a list of image sources
// and loads them in small batches using requestIdleCallback when available.
// Returns a cancel function to stop warming.
//
// Why: Preloading a large gallery of images synchronously can block the
// main thread and delay startup. Warming them in idle batches yields a
// smoother experience while still caching the assets for later use.
export function warmImagesInIdleBatches(
  sources,
  { batchSize = 8, timeoutMs = 120, fallbackDelayMs = 24 } = {}
) {
  if (typeof window === 'undefined') return () => {};

  const queue = uniq(sources);
  if (!queue.length) return () => {};

  let cancelled = false;
  let idleId = null;
  let timeoutId = null;

  const clearScheduled = () => {
    if (
      idleId != null &&
      typeof window.cancelIdleCallback === 'function'
    ) {
      window.cancelIdleCallback(idleId);
    }
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
    }
    idleId = null;
    timeoutId = null;
  };

  const runNextBatch = () => {
    idleId = null;
    timeoutId = null;
    if (cancelled || !queue.length) return;
    const batch = queue.splice(0, batchSize);
    Promise.all(batch.map((src) => loadImage(src)))
      .catch(() => {
        // ignore batch failures
      })
      .finally(() => {
        if (cancelled || !queue.length) return;
        scheduleNextBatch();
      });
  };

  const scheduleNextBatch = () => {
    if (cancelled || !queue.length) return;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(runNextBatch, {
        timeout: timeoutMs,
      });
      return;
    }
    timeoutId = window.setTimeout(runNextBatch, fallbackDelayMs);
  };

  // Start the first batch schedule.
  scheduleNextBatch();

  return () => {
    cancelled = true;
    clearScheduled();
  };
}